import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  Signal,
  SimpleChanges,
  ViewChild,
  ViewChildren
} from '@angular/core';
import {MenuItem, MessageService} from "primeng/api";
import {CosoService} from "../coso.service";
import {AsyncPipe, DatePipe, NgClass} from "@angular/common";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {Textarea} from "primeng/textarea";
import {MarkdownComponent, MarkdownPipe, MarkdownPipeOptions, MarkdownService, provideMarkdown} from "ngx-markdown";
import markdownToTxt from 'markdown-to-txt';
import {firstValueFrom, Subscription} from "rxjs";
import {Tooltip} from "primeng/tooltip";
import {Tag} from "primeng/tag";
import {toSignal} from "@angular/core/rxjs-interop";
import {Select, SelectChangeEvent} from "primeng/select";
import {AccordionModule} from 'primeng/accordion';
import {
  CosoActionRequest,
  CosoChat,
  CosoContext,
  CosoDecisionType,
  CosoFile,
  CosoFileType,
  CosoHistoryFile,
  CosoInteraction,
  CosoInteractionType,
  CosoMessage,
  CosoMessageType,
  CosoReasoningEffort,
  CosoReasoning,
  CosoStream,
  CosoStreamType,
  CosoToolCall
} from "../datamodel";
import {PanelModule} from "primeng/panel";
import {Button} from "primeng/button";
import {VoiceService} from "../voice.service";
import {Popover} from "primeng/popover";
import {FileUpload, FileUploadHandlerEvent} from "primeng/fileupload";
import {ChartModule} from "primeng/chart";
import {environment} from "@app/environment";
import {DeviceService} from "../../../../core/device.service";
import {SpeechLanguage, SpeechStreamService} from "../speech-stream.service";
import {AttachmentsService} from "../../../../core/attachments/attachments.service";
import {Menu} from "primeng/menu";
import {SanitizePasteDirective} from "../../../../core/sanitize-paste.directive";
import {Dialog} from "primeng/dialog";
import {InputText} from "primeng/inputtext";
import {RichTextEditorComponent} from "../../../../core/rich-text-editor/rich-text-editor.component";
import {KnowledgeBaseRequestsService} from "../../../knowledge-base-requests/knowledge-base-requests.service";
import {KBRequest, KBRequestStatus} from "../../../knowledge-base-requests/datamodel";
import {ActivitiesService} from "../../../../core/activities/activities.service";
import {ActivityType} from "../../../../core/activities/datamodel";
import {CosoConfig} from "../../coso-config/datamodel";
import {MIME_TYPES} from "../../../../core/attachments/datamodel";
import {CosoPromptsService} from "../../coso-prompts/coso-prompts.service";
import {CosoPrompt} from "../../coso-prompts/datamodel";
import {Checkbox} from "primeng/checkbox";
import {CosoLiveService} from "../coso-live.service";
import {SplitButton} from "primeng/splitbutton";

interface CosoPromptGroup {
  category?: string;
  prompts: CosoPrompt[];
}

interface PendingCosoFile {
  type: CosoFileType;
  fileName: string;
  mimeType: string;
  file: File;
  previewUrl: string;
}

type VoiceControlMode = 'dictate' | 'voice' | 'video';

// Component decorator defining metadata for the CosoChatDetailComponent
@Component({
  selector: 'app-coso-chat-detail',
  imports: [
    SanitizePasteDirective,
    AsyncPipe,
    DatePipe,
    ReactiveFormsModule,
    Textarea,
    FormsModule,
    MarkdownComponent,
    MarkdownPipe,
    Tooltip,
    Tag,
    Select,
    AccordionModule,
    PanelModule,
    Button,
    Popover,
    FileUpload,
    ChartModule,
    NgClass,
    Menu,
    Dialog,
    InputText,
    RichTextEditorComponent,
    Checkbox,
    SplitButton
  ],
  providers: [
    provideMarkdown(),
  ],
  templateUrl: './coso-chat-detail.component.html',
  styleUrl: './coso-chat-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CosoChatDetailComponent implements AfterViewInit, OnChanges, OnDestroy, OnInit {

  // View queries to get references to elements in the template
  @ViewChildren('messageItem') messageItems!: QueryList<ElementRef>;
  @ViewChildren('humanMessageItem') humanMessageItems!: QueryList<ElementRef>;
  @ViewChild('chat') chat?: ElementRef<HTMLElement>;
  @ViewChild('uploader') fileUploader?: FileUpload;
  @ViewChild('fileUpload') fileUploadPopover?: Popover;
  @ViewChild('knowledgeBaseEditor') knowledgeBaseEditor?: RichTextEditorComponent;
  @ViewChild('cosoPromptEditor') cosoPromptEditor?: RichTextEditorComponent;

  // Public properties for data binding and component state
  public chatInputMessage: string = '';
  readonly humanMessageMarkdownOptions: MarkdownPipeOptions = {markedOptions: {breaks: true}};
  @Input() selectedChat: CosoChat | null = null;
  @Output() chatUpdated = new EventEmitter<CosoChat>();
  public bottomSpacerHeight = 0;
  private chatMutationObserver?: MutationObserver;
  messageHistory: CosoMessage[] = [];
  $messageHistory: Subscription | undefined;
  $sendMessage: Subscription | undefined;
  $liveSession: Subscription | undefined;
  $transcription: Subscription | undefined;
  $s3SdownloadSub: Subscription | undefined;
  $createKnowledgeBaseRequest: Subscription | undefined;
  $createCosoPrompt: Subscription | undefined;
  $cosoPrompts: Subscription | undefined;
  $logActivity: Subscription | undefined;
  $updateChatTitle: Subscription | undefined;
  context: Signal<CosoContext | undefined>;
  liveMediaStream!: Signal<MediaStream | null>;
  loading = true;
  streamingResponse = false;
  selectedReasoningEffort: CosoReasoningEffort = 'medium';
  reasoningEffortOptions: { label: string; value: CosoReasoningEffort }[] = [];
  attachedFiles: PendingCosoFile[] = [];
  uploadingAttachments = false;
  private readonly MAX_ATTACHMENTS = 5;
  private readonly MAX_ATTACHMENT_SIZE = 4.5 * 1024 * 1024;
  public listening = false;
  public liveActive = false;
  private readonly allowedCosoConfigs: Signal<CosoConfig[]>;
  private voiceControlModeInitialized = false;
  public voiceControlMode: VoiceControlMode = 'dictate';
  public voiceControlMenuItems: MenuItem[] = [];

  public get liveAudioEnabled(): boolean {
    return !!environment.FEATURE_FLAGS.coso.enable_live_audio && this.getSelectedCosoConfig()?.live_audio_allowed === true;
  }

  public get liveVideoEnabled(): boolean {
    return !!environment.FEATURE_FLAGS.coso.enable_live_video && this.getSelectedCosoConfig()?.live_video_allowed === true;
  }

  public get liveModesEnabled(): boolean {
    return this.liveAudioEnabled || this.liveVideoEnabled;
  }

  private pendingScrollToLastHumanMessage = false;
  public selectedSpeechLanguage: SpeechLanguage = 'it';
  public speechLanguageOptions: { label: string; value: SpeechLanguage }[] = [
    {label: 'IT', value: 'it'},
    {label: 'EN', value: 'en'}
  ];
  public speechEnabled = false;
  public speaking = false;
  public knowledgeBaseDialogVisible = false;
  public knowledgeBaseTitle = '';
  public knowledgeBaseMarkdown = '';
  public knowledgeBaseLanguage: SpeechLanguage = this.selectedSpeechLanguage;
  public knowledgeBaseSaving = false;
  public cosoPromptDialogVisible = false;
  public cosoPromptTitle = '';
  public cosoPromptCategory = '';
  public cosoPromptText = '';
  public cosoPromptPublic = false;
  public cosoPromptSaving = false;
  public cosoPromptOptions: CosoPrompt[] = [];
  public cosoPromptSearchQuery = '';
  public cosoPromptsLoading = false;
  private readonly speechLanguageStorageKey = 'settings.component.coso.speechLanguage';
  private readonly speechEnabledStorageKey = 'settings.component.coso.speechEnabled';
  private speechStoppedForCurrentResponse = false;
  private speechPlaybackToken = 0;
  private screenWakeLock?: WakeLockSentinel;

  // Parses a string to a chart configuration object
  private chartConfigCache = new WeakMap<CosoToolCall, { result: string; parsed: any | null }>();
  private reasoningPanelsCollapsed = new WeakMap<CosoMessage, boolean>();
  private messageActionItems = new WeakMap<CosoMessage, MenuItem[]>();
  private loadedChatId: number | undefined;
  private loadedCosoPromptsForUserId: number | undefined;
  private titleUpdateInFlight = false;

  // Map s3Keys to presignedUrls so clicking on the same attachment doesn't call s3 multiple times
  private s3AttachmentsUrls = new Map<string, string>();
  private generatedImageUrls = new Map<string, string>();
  private loadingGeneratedImageKeys = new Set<string>();
  private generatedImagePreviewSubscriptions = new Subscription();
  private attachmentPreviewUrls = new Map<string, string>();
  private loadingAttachmentPreviewKeys = new Set<string>();
  private attachmentPreviewSubscriptions = new Subscription();

  // Expose utilities to the template
  protected readonly JSON = JSON;
  protected readonly CosoDecisionType = CosoDecisionType;
  protected readonly CosoMessageType = CosoMessageType;
  protected readonly environment = environment;

  // Constructor for dependency injection
  constructor(public cosoService: CosoService,
              private messageService: MessageService,
              private voiceService: VoiceService,
              public deviceService: DeviceService,
              public speechService: SpeechStreamService,
              private cosoLiveService: CosoLiveService,
              private attachmentsService: AttachmentsService,
              private cdr: ChangeDetectorRef,
              private markdownService: MarkdownService,
              private knowledgeBaseRequestsService: KnowledgeBaseRequestsService,
              private cosoPromptsService: CosoPromptsService,
              private activitiesService: ActivitiesService) {
    this.context = toSignal(this.cosoService.context$);
    this.allowedCosoConfigs = toSignal(this.cosoService.allowedCosoConfigs$, {initialValue: []});
    effect(() => this.updateVoiceControlModes());
    this.liveMediaStream = toSignal(this.cosoLiveService.activeMediaStream$, {initialValue: null});
    effect(() => {
      const moduleName = this.context()?.module_name;
      if(moduleName?.toLowerCase().includes('bedrock')) {
        this.reasoningEffortOptions = [
          {label: 'Low', value: 'low'},
          {label: 'Medium', value: 'medium'},
          {label: 'High', value: 'high'},
          {label: 'Max', value: 'max'}
        ];
      } else {
        this.reasoningEffortOptions = [
          {label: 'Minimal', value: 'minimal'},
          {label: 'Low', value: 'low'},
          {label: 'Medium', value: 'medium'},
          {label: 'High', value: 'high'}
        ];
      }
      this.selectedReasoningEffort = this.context()?.reasoning_effort ?? 'medium';
      const user = this.cosoService.user;
      if (user?.user_id && this.loadedCosoPromptsForUserId !== user.user_id) {
        this.loadCosoPrompts();
      }
    })
    this.loadSpeechLanguageSetting();
    this.loadSpeechEnabledSetting();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['selectedChat']) {
      this.openSelectedChat();
      this.updateVoiceControlModes();
    }
  }

  get acceptedAttachmentTypes(): string {
    return this.getSelectedSupportedAttachmentMimeTypes().join(',');
  }

  private openSelectedChat() {
    const selectedChat = this.selectedChat;
    const selectedChatId = selectedChat?.coso_chats_id;
    if (!selectedChatId) {
      if (this.liveActive) {
        this.stopCosoLive();
      }
      return;
    }

    if (this.loadedChatId === selectedChatId) {
      return;
    }

    if (this.liveActive) {
      this.stopCosoLive();
    }

    this.loadedChatId = selectedChatId;
    this.messageHistory = [];
    this.clearAttachedFiles();
    this.chatInputMessage = '';
    this.bottomSpacerHeight = 0;
    this.s3AttachmentsUrls.clear();
    this.loadingAttachmentPreviewKeys.clear();
    this.attachmentPreviewSubscriptions.unsubscribe();
    this.attachmentPreviewSubscriptions = new Subscription();

    this.cosoService.selectCosoChat(selectedChat);
    if (this.isSpeechLanguageSelection(selectedChat.language)) {
      this.selectedSpeechLanguage = selectedChat.language;
      this.applySpeechLanguageSelection();
    }
    this.loadMessageHistory();
  }

  // Lifecycle hook that runs when the component is initialized
  ngOnInit() {

    // Customize ngx-markdown link rendering to custom render links to our attachments
    const defaultLinkRenderer = this.markdownService.renderer.link;
    this.markdownService.renderer.link = function (link)  {
      if(!link.href.startsWith('https://attachments/')) {
        return defaultLinkRenderer.call(this, link)
      }

      return `<a href="${link.href.replace('https://', '')}" class="not-prose inline-flex max-w-full items-center gap-1.5 rounded-full border border-blue-200 bg-blue-100 px-3 py-1 text-xs font-medium text-blue-700 no-underline transition-colors hover:bg-blue-200 dark:border-blue-400/40 dark:bg-blue-500/15 dark:text-blue-200 dark:hover:bg-blue-500/25">` +
                `<i class="pi pi-link text-[10px]" aria-hidden="true"></i>` +
                `<span class="max-w-64 truncate">${link.text}</span>` +
             `</a>`
    }

    this.$transcription = this.voiceService.transcript.subscribe(text => {
      if (!this.listening) {
        return;
      }
      this.chatInputMessage = text;
      this.cdr.markForCheck();
    });
  }

  onSpeechLanguageChange(event: SelectChangeEvent) {
    if (this.isSpeechLanguageSelection(event.value)) {
      this.selectedSpeechLanguage = event.value;
    }

    this.applySpeechLanguageSelection();
    localStorage.setItem(this.speechLanguageStorageKey, JSON.stringify(this.selectedSpeechLanguage));
  }

  switchSpeechEnabled() {
    if (this.speechEnabled) {
      this.speechPlaybackToken++;
      this.speechService.stop();
      this.speaking = false;
    } else {
      this.applySpeechLanguageSelection();
    }

    this.speechEnabled = !this.speechEnabled;
    localStorage.setItem(this.speechEnabledStorageKey, JSON.stringify(this.speechEnabled));
  }

  private loadSpeechLanguageSetting() {
    const speechLanguageJson = localStorage.getItem(this.speechLanguageStorageKey);
    if (speechLanguageJson) {
      try {
        const speechLanguage = JSON.parse(speechLanguageJson);
        if (this.isSpeechLanguageSelection(speechLanguage)) {
          this.selectedSpeechLanguage = speechLanguage;
        }
      } catch (error) {
        console.warn('Invalid Coso speech language setting:', error);
      }
    }

    this.applySpeechLanguageSelection();
  }

  private loadSpeechEnabledSetting() {
    const speechEnabledJson = localStorage.getItem(this.speechEnabledStorageKey);
    if (speechEnabledJson) {
      try {
        const speechEnabled = JSON.parse(speechEnabledJson);
        if (typeof speechEnabled === 'boolean') {
          this.speechEnabled = speechEnabled;
        }
      } catch (error) {
        console.warn('Invalid Coso speech enabled setting:', error);
      }
    }
  }

  private applySpeechLanguageSelection() {
    this.speechPlaybackToken++;
    this.speechService.setLanguage(this.selectedSpeechLanguage);
    this.cosoService.updateCurrentContext({language: this.selectedSpeechLanguage});
    this.speaking = false;
  }

  private isSpeechLanguageSelection(value: unknown): value is SpeechLanguage {
    return this.speechLanguageOptions.some(option => option.value === value);
  }

  getSpeechLanguageIcon(): string {
    return 'pi pi-volume-up';
  }

  private updateVoiceControlModes() {
    if (!this.voiceControlModeInitialized && this.getSelectedCosoConfig()) {
      this.voiceControlModeInitialized = true;
      if (this.deviceService.isMobile && this.liveAudioEnabled) {
        this.setVoiceControlMode('voice');
      }
    }
    if (!this.isVoiceControlModeAvailable(this.voiceControlMode)) {
      this.setVoiceControlMode('dictate');
    }

    this.voiceControlMenuItems = [
      {
        label: 'Dictate',
        icon: 'coso-material-icon coso-material-mic',
        command: () => this.setVoiceControlMode('dictate')
      },
      ...(this.liveAudioEnabled ? [{
        label: 'Voice mode',
        icon: 'coso-material-icon coso-material-graphic-eq',
        command: () => this.setVoiceControlMode('voice')
      }] : []),
      ...(this.liveVideoEnabled ? [{
        label: 'Video mode',
        icon: 'coso-material-icon coso-material-videocam',
        command: () => this.setVoiceControlMode('video')
      }] : [])
    ];
    this.syncVoiceControlMenuSelection();
    this.cdr.markForCheck();
  }

  private syncVoiceControlMenuSelection() {
    const selectedClass = 'bg-surface-100 dark:bg-surface-800';
    const selectedLabel = this.voiceControlMode === 'voice'
      ? 'Voice mode'
      : this.voiceControlMode === 'video'
        ? 'Video mode'
        : 'Dictate';
    for (const item of this.voiceControlMenuItems) {
      item.linkClass = item.label === selectedLabel ? selectedClass : '';
    }
  }

  setVoiceControlMode(mode: VoiceControlMode) {
    if (!this.isVoiceControlModeAvailable(mode)) {
      return;
    }

    if (mode === this.voiceControlMode) {
      return;
    }

    if (this.voiceControlMode === 'dictate' && this.listening) {
      this.stopListening();
    }
    if (this.voiceControlMode !== 'dictate' && this.liveActive) {
      this.stopCosoLive();
    }

    this.voiceControlMode = mode;
    this.syncVoiceControlMenuSelection();
    this.cdr.markForCheck();
  }

  getVoiceControlTooltip(): string {
    if (this.voiceControlMode === 'video') {
      return this.liveActive ? 'Stop video mode' : 'Start video mode';
    }

    if (this.voiceControlMode === 'voice') {
      return this.liveActive ? 'Stop voice mode' : 'Start voice mode';
    }

    return this.listening ? 'Stop dictation' : 'Dictate';
  }

  private isVoiceControlModeAvailable(mode: VoiceControlMode): boolean {
    return mode === 'dictate'
      || (mode === 'voice' && this.liveAudioEnabled)
      || (mode === 'video' && this.liveVideoEnabled);
  }

  toggleVoiceControl() {
    if (this.voiceControlMode !== 'dictate') {
      this.toggleCosoLive();
    } else if (this.listening) {
      this.stopListening();
    } else {
      this.startListening();
    }
  }

  @HostListener('document:visibilitychange')
  onDocumentVisibilityChange() {
    if (document.hidden && this.liveActive) {
      this.stopCosoLive();
      return;
    }

    if (document.visibilityState === 'visible' && this.liveActive) {
      void this.requestScreenWakeLock();
    }
  }

  @HostListener('window:pagehide')
  onPageHide() {
    if (this.liveActive) {
      this.stopCosoLive();
    }
  }

  // Lifecycle hook that runs when the component is destroyed
  ngAfterViewInit() {
    this.observeChatForLiveVideo();
  }

  ngOnDestroy() {
    this.chatMutationObserver?.disconnect();
    this.$sendMessage?.unsubscribe();
    this.$liveSession?.unsubscribe();
    this.$messageHistory?.unsubscribe();
    this.$s3SdownloadSub?.unsubscribe();
    this.generatedImagePreviewSubscriptions.unsubscribe();
    this.attachmentPreviewSubscriptions.unsubscribe();
    this.$createKnowledgeBaseRequest?.unsubscribe();
    this.$createCosoPrompt?.unsubscribe();
    this.$cosoPrompts?.unsubscribe();
    this.$logActivity?.unsubscribe();
    this.$updateChatTitle?.unsubscribe();
    this.clearAttachedFiles();
    if (this.$transcription) {
      this.$transcription.unsubscribe();
    }
    this.stopListening(true);
    this.cosoLiveService.stop();
    this.releaseScreenWakeLock();
    this.speechService.stop();
  }

  // Loads the message history from the Coso service
  loadMessageHistory() {
    if (!this.selectedChat?.coso_chats_id) {
      return;
    }

    this.loading = true;
    this.$messageHistory = this.cosoService.getMessageHistory().subscribe({
      next: (history: CosoStream[]) => {
        this.messageHistory = [];
        let toolCalls: CosoToolCall[] = [];
        let reasoning: CosoReasoning[] = [];


        // Process each stream in the history to build the message list
        for (const cosoStream of history) {
          if (cosoStream.type === CosoStreamType.HUMAN_MESSAGE) {
            this.messageHistory.push({
              type: CosoMessageType.HUMAN,
              date: new Date(cosoStream.timestamp),
              author: 'you',
              content: cosoStream.content,
              files: cosoStream.files,
              summarization: cosoStream.summarization
            })
          } else if (cosoStream.type === CosoStreamType.REASONING) {
            reasoning.push(cosoStream);
          } else if (cosoStream.type === CosoStreamType.TOOL_REQUEST) {
            for(const call of cosoStream.calls) {
              const idx = toolCalls.findIndex(t => t.id === call.id)
              if(idx === -1) {
                toolCalls.push(call)
              }
            }
          } else if (cosoStream.type === CosoStreamType.TOOL_RESPONSE) {
            for (const toolCall of cosoStream.calls) {
              const call = toolCalls.find(c => toolCall.id === c.id)
              if (!call) {
                toolCalls.push(toolCall);
                continue;
              }

              call.status = toolCall.status;
              call.result = toolCall.result;
              call.artifact = toolCall.artifact;
            }
          } else if (cosoStream.type === CosoStreamType.AI_MESSAGE || cosoStream.type === CosoStreamType.AI_PARTIAL_MESSAGE) {

            // Sometimes there is an AI message and an AI_PARTIAL_MESSAGE one after the other we need to merge them in a single AI message
            let lastMessage = this.messageHistory[this.messageHistory.length - 1]
            if(lastMessage && lastMessage.type === CosoMessageType.AI) {
              lastMessage.content += cosoStream.content;
              lastMessage.toolCalls!.push(...toolCalls);
              if (reasoning.length) {
                if (!lastMessage.reasoning) {
                  lastMessage.reasoning = [];
                }
                lastMessage.reasoning.push(...reasoning);
              }
            } else {
              lastMessage = {
                type: CosoMessageType.AI,
                date: new Date(cosoStream.timestamp),
                author: 'Coso',
                content: cosoStream.content,
                toolCalls: toolCalls,
                reasoning: reasoning.length ? [...reasoning] : undefined
              }
              this.messageHistory.push(lastMessage)
            }

            // Replace attachment reference tokens with markdown links
            // Markdown links to our attachments will then be custom rendered by ngx-markdown
            lastMessage.content = this.replaceAttachmentReferenceTokensWithMarkdownLinks(lastMessage.content);

            toolCalls = [];
            reasoning = [];
          } else if (cosoStream.type === CosoStreamType.MODEL_ERROR) {
            let lastMessage = this.messageHistory[this.messageHistory.length - 1]
            if(lastMessage && lastMessage.type === CosoMessageType.AI) {
              if (!lastMessage.errors) {
                lastMessage.errors = [];
              }
              lastMessage.errors.push(cosoStream)
            } else {
              lastMessage = {
                type: CosoMessageType.AI,
                date: new Date(cosoStream.timestamp),
                author: 'Coso',
                toolCalls: toolCalls,
                reasoning: reasoning.length ? [...reasoning] : undefined,
                errors: [cosoStream]
              }
              this.messageHistory.push(lastMessage)
            }
          }
        }
        // If no history, display a welcome message
        if (!this.messageHistory.length) {
          this.messageHistory.push({
            type: CosoMessageType.AI,
            date: new Date(),
            author: 'Coso',
            content: environment.COSO_WELCOME_MSG.replace('[user]',this.cosoService.user!.firstname!),
            welcomeMsg: true
          })
        }
        this.loadGeneratedImagePreviews();
        this.loading = false;
        this.cdr.markForCheck();
        this.scrollToBottom();
      },
      error: error => {
        console.error(error);
        this.loading = false;
      }
    })
  }

  // Returns the last message sent by the user
  public getLastHumanMessage(): CosoMessage | undefined {
    return this.messageHistory.filter(msg => msg.author === 'you').at(-1);
  }

  getChartConfig(toolCall: CosoToolCall) {
    const result = toolCall.result;
    if (!result) {
      return null;
    }

    const cached = this.chartConfigCache.get(toolCall);
    if (cached && cached.result === result) {
      return cached.parsed;
    }

    let parsed: any | null = null;
    try {
      parsed = JSON.parse(result);
    } catch (error) {
      console.warn('Invalid chart config tool result', error);
    }

    this.chartConfigCache.set(toolCall, {result, parsed});
    return parsed;
  }

  isReasoningPanelCollapsed(message: CosoMessage): boolean {
    return this.reasoningPanelsCollapsed.get(message) ?? true;
  }

  setReasoningPanelCollapsed(message: CosoMessage, collapsed: boolean | undefined) {
    this.reasoningPanelsCollapsed.set(message, collapsed ?? false);
  }

  toggleReasoningPanel(message: CosoMessage) {
    this.setReasoningPanelCollapsed(message, !this.isReasoningPanelCollapsed(message));
  }

  getReasoningMarkdown(message: CosoMessage): string {
    let reasoning = (message.reasoning ?? []).map(reasoning => reasoning.content);

    if(reasoning.length && reasoning[0].startsWith('**')) {
      return reasoning.join('\n\n');
    }

    return reasoning.join('');
  }

  getReasoningPreview(message: CosoMessage): string | null {
    const reasoningItems = message.reasoning ?? [];

    // All Google reasoning starts with ** and it has nice headings
    if(reasoningItems.length && reasoningItems[0].content.startsWith('**')) {
      for (let i = reasoningItems.length - 1; i >= 0; i--) {
        const reasoning = reasoningItems[i];
        const firstLine = reasoning.content
          .split(/\r?\n/)
          .map(line => line.trim())
          .find(line => line.length > 0);

        if (firstLine) {
          return firstLine.replace(/^\*\*(.*)\*\*$/, '$1').trim();
        }
      }
    } else {
      const text = this.getReasoningMarkdown(message);
      const lastDot = text.lastIndexOf(".");

      // Fewer than 1 dot
      if (lastDot === -1) return text;

      const secondLastDot = text.lastIndexOf(".", lastDot - 1);

      // Fewer than 2 dots
      if (secondLastDot === -1) return text;

      return text.slice(secondLastDot + 1, lastDot).trim();
    }



    return null;
  }

  // Sends an interaction to the Coso service
  sendMessageToCoso(interaction: CosoInteraction) {
    this.loading = true;
    this.streamingResponse = true;
    this.speechPlaybackToken++;
    this.speechStoppedForCurrentResponse = false;
    if(this.speechEnabled) this.speechService.unlockAudio(); // needed for iOS

    if (this.selectedChat) {
      const updatedChat = {
        ...this.selectedChat,
        m_date: new Date().toISOString()
      };
      this.selectedChat = updatedChat;
      this.chatUpdated.emit(updatedChat);
    }

    if (!this.$sendMessage?.closed) {
      this.$sendMessage?.unsubscribe()
    }

    this.pendingScrollToLastHumanMessage = this.messageHistory[this.messageHistory.length - 1]?.type === CosoMessageType.HUMAN;

    console.log('Sending:', interaction)
    console.log('History:', this.messageHistory)

    // Get current AI message or create a new one
    let aiMessage: CosoMessage = this.messageHistory[this.messageHistory.length - 1]
    if (aiMessage.type !== CosoMessageType.AI) {
      aiMessage = {
        type: CosoMessageType.AI,
        date: new Date(),
        author: 'Coso',
        content: '',
      }
      this.messageHistory.push(aiMessage);
    }

    console.log('History:', this.messageHistory)

    // Subscribe to the Coso stream for real-time updates
    this.$sendMessage = this.cosoService.sendMessageToCosoStream(interaction).subscribe({
      next: (cosoStream: CosoStream) => {
        this.applyCosoStreamToAiMessage(cosoStream, aiMessage);
        this.cdr.markForCheck();
      },
      error: error => {
        this.loading = false;
        this.streamingResponse = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error.message,
          sticky: false,
          life: 5000
        });
        this.cdr.markForCheck();
      },
      complete: () => {
        this.loading = false;
        this.streamingResponse = false;
        if (this.speechEnabled && !this.speechStoppedForCurrentResponse) {
          const playbackToken = this.speechPlaybackToken;
          this.speechService.flush().then(() => {
            if (playbackToken !== this.speechPlaybackToken) {
              return;
            }
            this.speaking = false;
            this.cdr.markForCheck();
          });
        }
        if (this.bottomSpacerHeight > 0) {
          this.updateBottomSpacerForLastHumanMessage();
        }
        this.cdr.markForCheck();
      }
    })

    this.chatInputMessage = '';
    if (this.pendingScrollToLastHumanMessage) {
      this.scrollToLastHumanMessage();
      this.pendingScrollToLastHumanMessage = false;
    } else {
      this.scrollToLastMessage();
    }
    this.cdr.markForCheck();
  }

  stopCosoResponse() {
    if (!this.streamingResponse) {
      return;
    }

    this.$sendMessage?.unsubscribe();
    this.loading = false;
    this.streamingResponse = false;
    this.pendingScrollToLastHumanMessage = false;
    this.speechPlaybackToken++;
    this.speechStoppedForCurrentResponse = true;
    this.speechService.stop();
    this.speaking = false;
    this.cdr.markForCheck();
  }

  // Sends a message from the user to the chat
  async sendHumanMessage() {
    if(this.listening) this.stopListening(true);
    if(this.speaking) {
      this.speechPlaybackToken++;
      this.speechService.stop();
      this.speaking = false;
    }
    if (!this.canSendHumanMessage()) {
      return;
    }

    if (!this.validateAttachedFilesBeforeSend()) {
      return;
    }

    this.uploadingAttachments = true;
    this.cdr.markForCheck();

    try {
      const files = await this.uploadAttachedFiles();
      const userMessage: CosoMessage = {
        type: CosoMessageType.HUMAN,
        date: new Date(),
        content: this.chatInputMessage.trim(),
        files,
        author: 'you'
      };

      this.clearAttachedFiles();
      this.uploadingAttachments = false;
      this.messageHistory.push(userMessage);
      this.renameDefaultChatFromFirstMessage(userMessage.content);

      this.sendMessageToCoso({
        type: CosoInteractionType.MESSAGE,
        content: userMessage.content,
        files
      });
    } catch (error) {
      this.uploadingAttachments = false;
      this.messageService.add({
        severity: 'error',
        summary: 'Attachment upload failed',
        detail: error instanceof Error ? error.message : 'Could not upload the attachment.',
        sticky: false,
        life: 5000
      });
      this.cdr.markForCheck();
    }
  }

  canSendHumanMessage(): boolean {
    return !this.loading && !this.streamingResponse && !this.uploadingAttachments && !!this.chatInputMessage.trim();
  }

  getChatInputPlaceholder(): string {
    if (this.attachedFiles.length) {
      return 'Please also type a message here when working with files.';
    }

    return `Ask ${environment.COSO_NAME}...`;
  }

  private validateAttachedFilesBeforeSend(): boolean {
    const supportedFiles = this.attachedFiles.filter(file => {
      return this.isSupportedAttachmentMimeType(file.mimeType) && file.file.size <= this.MAX_ATTACHMENT_SIZE;
    });

    if (supportedFiles.length === this.attachedFiles.length) {
      return true;
    }

    for (const file of this.attachedFiles) {
      if (!supportedFiles.includes(file)) {
        URL.revokeObjectURL(file.previewUrl);
      }
    }
    this.attachedFiles = supportedFiles;
    this.showUnsupportedAttachmentMessage();
    this.cdr.markForCheck();
    return false;
  }

  private async uploadAttachedFiles(): Promise<CosoFile[]> {
    const chatId = this.selectedChat?.coso_chats_id;
    if (!chatId) {
      throw new Error('Select a chat before attaching files.');
    }

    const bucket = environment.aws.s3.attachments.bucket;
    const region = environment.aws.s3.attachments.region;
    const uploadedFiles: CosoFile[] = [];

    for (const pendingFile of this.attachedFiles) {
      const key = `coso-chat-uploads/${chatId}/${crypto.randomUUID()}`;
      const uploadUrl = await firstValueFrom(this.attachmentsService.getS3UploadURL(
        bucket,
        key,
        region
      ));
      if (!uploadUrl) {
        throw new Error(`Could not prepare ${pendingFile.fileName} for upload.`);
      }

      const uploadedFile: CosoFile = {
        bucket,
        region,
        key,
        fileName: pendingFile.fileName,
        mimeType: pendingFile.mimeType,
        type: pendingFile.type,
        size: pendingFile.file.size
      };
      uploadedFiles.push(uploadedFile);

      const response = await fetch(uploadUrl, {
        method: 'PUT',
        body: pendingFile.file
      });
      if (!response.ok) {
        throw new Error(`Could not upload ${pendingFile.fileName}.`);
      }

    }

    return uploadedFiles;
  }

  private renameDefaultChatFromFirstMessage(content: string | undefined) {
    const trimmedContent = content?.trim();
    if (!trimmedContent || !this.selectedChat?.coso_chats_id || this.selectedChat.chat_title !== 'New chat' || this.titleUpdateInFlight) {
      return;
    }

    const chatTitle = trimmedContent.length > 64 ? `${trimmedContent.slice(0, 61)}...` : trimmedContent;
    const updatedChat: CosoChat = {
      ...this.selectedChat,
      chat_title: chatTitle
    };

    this.titleUpdateInFlight = true;
    this.selectedChat = updatedChat;
    this.chatUpdated.emit(updatedChat);

    this.$updateChatTitle = this.cosoService.updateCosoChat(updatedChat).subscribe({
      next: savedChat => {
        this.titleUpdateInFlight = false;
        const normalizedChat = savedChat?.coso_chats_id ? savedChat : updatedChat;
        this.selectedChat = normalizedChat;
        this.chatUpdated.emit(normalizedChat);
        this.cdr.markForCheck();
      },
      error: error => {
        this.titleUpdateInFlight = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error.message,
          sticky: false,
          life: 5000
        });
        this.cdr.markForCheck();
      }
    });
  }

  private applyCosoStreamToAiMessage(cosoStream: CosoStream, aiMessage: CosoMessage) {
    if (cosoStream.type === CosoStreamType.AI_PARTIAL_MESSAGE && cosoStream.content) {
      aiMessage.content += cosoStream.content;
      aiMessage.content = this.replaceAttachmentReferenceTokensWithMarkdownLinks(aiMessage.content);

      if(this.speechEnabled && !this.speechStoppedForCurrentResponse && !this.liveActive) {
        this.speaking = true;
        this.speechService.processChunk(cosoStream.content);
      }

      if (this.pendingScrollToLastHumanMessage) {
        this.scrollToLastHumanMessage();
        this.pendingScrollToLastHumanMessage = false;
      } else if (this.bottomSpacerHeight > 0) {
        this.updateBottomSpacerForLastHumanMessage();
      }
    } else if (cosoStream.type === CosoStreamType.TOOL_REQUEST) {
      if (!aiMessage.toolCalls) {
        aiMessage.toolCalls = [];
      }

      for(const call of cosoStream.calls) {
        const idx = aiMessage.toolCalls.findIndex(t => t.id === call.id)
        if(idx === -1) {
          aiMessage.toolCalls.push(call)
        } else {
          aiMessage.toolCalls[idx].toolArgs = call.toolArgs;
        }
      }
    } else if (cosoStream.type === CosoStreamType.TOOL_RESPONSE) {
      if (!aiMessage.toolCalls) {
        aiMessage.toolCalls = [];
      }

      for (const call of cosoStream.calls) {
        const toolCall = aiMessage.toolCalls!.find(c => c.id === call.id);
        if (!toolCall) {
          aiMessage.toolCalls!.push(call);
          this.loadGeneratedImagePreview(call);
          continue;
        }

        toolCall.status = call.status;
        toolCall.result = call.result;
        toolCall.artifact = call.artifact;
        this.loadGeneratedImagePreview(toolCall);
      }
    } else if (cosoStream.type === CosoStreamType.INTERRUPT) {
      if (!aiMessage.interrupts) {
        aiMessage.interrupts = [];
      }

      aiMessage.interrupts.push(cosoStream)
    } else if (cosoStream.type === CosoStreamType.REASONING) {
      if(!aiMessage.reasoning) {
        aiMessage.reasoning = [];
      }

      aiMessage.reasoning.push(cosoStream)
    } else if (cosoStream.type === CosoStreamType.REMOVE_MESSAGE) {
      if(cosoStream.messageId === '__remove_all__') {
        aiMessage.content = '';
      }
    } else if (cosoStream.type === CosoStreamType.MODEL_ERROR) {
      if(!aiMessage.errors) {
        aiMessage.errors = [];
      }
      aiMessage.errors.push(cosoStream)
    }
  }

  // Sends a response to an interrupt from the AI
  sendInterruptResponse() {
    const aiMessage = this.messageHistory[this.messageHistory.length - 1]
    if (aiMessage.type !== CosoMessageType.AI) {
      console.error('Error: sending interrupt response but last message was non-AI')
      return;
    }

    const lastInterrupt = aiMessage.interrupts?.filter(s => s.type === CosoStreamType.INTERRUPT).at(-1)
    if (!lastInterrupt) {
      console.error('Error: sending interrupt response but there are no interrupts')
      return;
    }

    // Send interrupt response only if a decision has been taken for all its actions
    if (lastInterrupt.actionRequests.every(a => a.decision)) {
      this.sendMessageToCoso({
        type: CosoInteractionType.INTERRUPT_RESPONSE,
        decisions: lastInterrupt.actionRequests.map(a => ({
          type: a.decision,
          message: a.decision === CosoDecisionType.APPROVE ? '' : 'L\'utente ha rifiutato la richiesta per l\'uso del tool' // TODO: reason for the rejection
        }))
      })
    }
  }

  // Approves an action request from the AI
  approveActionRequest(actionRequest: CosoActionRequest) {
    actionRequest.decision = CosoDecisionType.APPROVE;
    this.sendInterruptResponse();
  }

  // Rejects an action request from the AI
  rejectActionRequest(actionRequest: CosoActionRequest) {
    actionRequest.decision = CosoDecisionType.REJECT;
    this.sendInterruptResponse();
  }

  // Scrolls the chat to the last message
  scrollToLastMessage(): void {
    setTimeout(() => {
      const lastItem = this.messageItems.last;
      const chatContainer = this.chat?.nativeElement;
      if (!lastItem || !chatContainer) {
        return;
      }

      chatContainer.scrollTo({
        behavior: 'smooth',
        top: this.getElementTopInContainer(chatContainer, lastItem.nativeElement)
      });
    });
  }

  // Scrolls the chat to the last human message
  scrollToLastHumanMessage(): void {
    setTimeout(() => {
      const lastHumanItem = this.humanMessageItems.last;
      const chatContainer = this.chat?.nativeElement;
      if (!lastHumanItem || !chatContainer) {
        return;
      }

      this.bottomSpacerHeight = this.getRequiredBottomSpacerHeight(chatContainer, lastHumanItem.nativeElement);
      // Ensure spacer is applied before computing/setting the final scroll position.
      this.cdr.detectChanges();

      requestAnimationFrame(() => {
        chatContainer.scrollTo({
          behavior: 'smooth',
          top: this.getElementTopInContainer(chatContainer, lastHumanItem.nativeElement)
        });
      });
    });
  }

  // Scrolls the chat to the bottom
  scrollToBottom(): void {
    setTimeout(() => {
      const chatContainer = this.chat?.nativeElement;
      if (!chatContainer) {
        return;
      }

      chatContainer.scrollTo({
        behavior: 'smooth',
        top: chatContainer.scrollHeight
      });
    });
  }

  // Handles keydown events in the chat input
  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'ArrowUp') {
      if (!this.chatInputMessage || this.chatInputMessage === '') {
        this.chatInputMessage = this.getLastHumanMessage()?.content || '';
      }
    }

    // Check for Enter key
    if (event.key === 'Enter') {
      // Only send if Shift is NOT held down
      if (!event.shiftKey) {
        event.preventDefault(); // Stop the line break from being added
        this.sendHumanMessage(); // Send the message
      }
    }
  }

  onReasoningEffortChange(effort: CosoReasoningEffort) {
    this.selectedReasoningEffort = effort;
    this.cosoService.updateCurrentContext({reasoning_effort: effort});
  }

  // Delegated event handler for attachment links
  @HostListener('click', ['$event'])
  onAttachmentLinkInteraction(event: MouseEvent) {
    const target = event.target as HTMLElement | null;
    const link = target?.closest('a[href^="attachments"]') as HTMLAnchorElement | null;
    if (!link) {
      return;
    }

    event.preventDefault();

    const s3Key = link.getAttribute('href');
    if (!s3Key) {
      return;
    }

    this.onAttachmentLinkClick(this.decodeAttachmentS3Key(s3Key));
  }

  private onAttachmentLinkClick(s3Key: string) {
    let cachedUrl = this.s3AttachmentsUrls.get(s3Key);
    if(cachedUrl) {
      window.open(cachedUrl, '_blank', 'noopener');
      return;
    }

    this.$s3SdownloadSub = this.attachmentsService.getS3DownloadURL(
        environment.aws.s3.attachments.bucket,
        s3Key,
        environment.aws.s3.attachments.region
    ).subscribe({
      next: (url) => {
        if(!url) {
          return;
        }

        this.s3AttachmentsUrls.set(s3Key, url)
        window.open(url, '_blank', 'noopener');
      },
      error: (error) => {
        this.messageService.add({severity: 'error', summary: 'Error', detail: error.message, sticky: true});
      }
    })
  }

  isGeneratedImage(toolCall: CosoToolCall): boolean {
    return toolCall.toolName === 'Generate-Image'
      && toolCall.status === 'success'
      && !!toolCall.artifact?.generatedImage?.key;
  }

  getGeneratedImageUrl(toolCall: CosoToolCall): string | undefined {
    return toolCall.artifact?.generatedImage?.key
      ? this.generatedImageUrls.get(toolCall.artifact.generatedImage.key)
      : undefined;
  }

  downloadGeneratedImage(toolCall: CosoToolCall) {
    const image = toolCall.artifact?.generatedImage;
    if (!image) {
      return;
    }

    this.downloadGeneratedFile(image);
  }

  isGeneratedPdf(toolCall: CosoToolCall): boolean {
    return toolCall.toolName === 'PDF-Generation'
      && toolCall.status === 'success'
      && !!toolCall.artifact?.generatedPdf?.key;
  }

  downloadGeneratedPdf(toolCall: CosoToolCall) {
    const pdf = toolCall.artifact?.generatedPdf;
    if (!pdf) {
      return;
    }

    this.downloadGeneratedFile(pdf);
  }

  private downloadGeneratedFile(file: { key: string; filename: string }) {
    this.$s3SdownloadSub?.unsubscribe();
    this.$s3SdownloadSub = this.attachmentsService.getS3DownloadURL(
      environment.aws.s3.attachments.bucket,
      file.key,
      environment.aws.s3.attachments.region,
      undefined,
      file.filename
    ).subscribe({
      next: (url) => {
        if (!url) {
          return;
        }

        void this.saveGeneratedFile(url, file.filename);
      },
      error: (error) => {
        this.messageService.add({severity: 'error', summary: 'Error', detail: error.message, sticky: true});
      }
    });
  }

  private loadGeneratedImagePreviews() {
    this.generatedImagePreviewSubscriptions.unsubscribe();
    this.generatedImagePreviewSubscriptions = new Subscription();
    this.generatedImageUrls.clear();
    this.loadingGeneratedImageKeys.clear();

    for (const message of this.messageHistory) {
      for (const toolCall of message.toolCalls ?? []) {
        this.loadGeneratedImagePreview(toolCall);
      }
    }
  }

  private loadGeneratedImagePreview(toolCall: CosoToolCall) {
    if (!this.isGeneratedImage(toolCall)) {
      return;
    }

    const key = toolCall.artifact.generatedImage!.key;
    if (this.generatedImageUrls.has(key) || this.loadingGeneratedImageKeys.has(key)) {
      return;
    }

    this.loadingGeneratedImageKeys.add(key);
    this.generatedImagePreviewSubscriptions.add(
      this.attachmentsService.getS3DownloadURL(
        environment.aws.s3.attachments.bucket,
        key,
        environment.aws.s3.attachments.region
      ).subscribe({
        next: (url) => {
          if (url) {
            this.generatedImageUrls.set(key, url);
          }
          this.loadingGeneratedImageKeys.delete(key);
          this.cdr.markForCheck();
        },
        error: () => {
          this.loadingGeneratedImageKeys.delete(key);
          this.cdr.markForCheck();
        }
      })
    );
  }

  private async saveGeneratedFile(url: string, filename: string) {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Download failed with status ${response.status}`);
      }

      const objectUrl = URL.createObjectURL(await response.blob());
      this.triggerBrowserDownload(objectUrl, filename);
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
    } catch (error) {
      // The signed URL also instructs S3 to serve an attachment, so this fallback remains a download.
      this.triggerBrowserDownload(url, filename);
    }
  }

  private triggerBrowserDownload(url: string, filename: string) {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  private replaceAttachmentReferenceTokensWithMarkdownLinks(content: string | undefined): string | undefined {
    if(!content) {
      return content;
    }

    return content.replace(
      /\[\[ATTACHMENT_REF:([^:\]]+):([^:\]]+):([^:\]]+):([^\]]+)\]\]/g,
      (_token, tableName: string, recordId: string, attachmentId: string, encodedFileName: string) => {
        return this.buildAttachmentMarkdownLink(
            tableName,
            recordId,
            attachmentId,
            this.decodeAttachmentReferenceFileName(encodedFileName)
        )
      }
    );
  }

  private buildAttachmentMarkdownLink(tableName: string, recordId: string, attachmentId: string, fileName: string): string {
    const normalizedFileName = fileName.trim() || attachmentId;
    const href = `https://attachments/${this.encodeAttachmentKeySegment(tableName)}/${this.encodeAttachmentKeySegment(recordId)}/${this.encodeAttachmentKeySegment(normalizedFileName)}`;
    const linkText = this.escapeMarkdownLinkText(normalizedFileName);
    return `[${linkText}](${href})`;
  }

  private decodeAttachmentReferenceFileName(encodedFileName: string): string {
    // This replacement is needed because the response is HTTP streamed so it might
    // include an escaped \n where it was split in two streams
    let normalized = encodedFileName.replace(/\\n/g, '');
    normalized = normalized.replace(/\s+/g, "");

    try {
      const bytes = Uint8Array.from(atob(normalized), char => char.charCodeAt(0));
      return new TextDecoder().decode(bytes);
    } catch(error) {
      console.error(error)
      console.error("Failed to base64 decode filename:", encodedFileName)
      return "Fonte";
    }
  }

  private encodeAttachmentKeySegment(segment: string): string {
    return encodeURIComponent(segment).replace(/[!'()*]/g, char => `%${char.charCodeAt(0).toString(16).toUpperCase()}`);
  }

  private escapeMarkdownLinkText(text: string): string {
    return text.replace(/([\\\[\]])/g, '\\$1');
  }

  private decodeAttachmentS3Key(key: string): string {
    try {
      return decodeURIComponent(key);
    } catch {
      return key;
    }
  }

  // Copies tool data to the clipboard
  async copyToolData(toolData: string) {
    try {
      await navigator.clipboard.writeText(JSON.stringify(toolData, null, 2));
      this.messageService.add({
        severity: 'success',
        summary: 'Tool data copied',
        detail: 'Tool response data copied to the clipboard.',
        sticky: false,
        life: 3000
      });
    } catch (err) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Tool response data falied to copy',
        sticky: false,
        life: 5000
      });
    }
  }

  getActionRequestHeader(actionRequest: CosoActionRequest): string {
    return actionRequest.question || actionRequest.content || `Approve ${actionRequest.toolName}?`;
  }

  getActionRequestFields(actionRequest: CosoActionRequest) {
    if (actionRequest.display?.fields?.length) {
      return actionRequest.display.fields;
    }

    return Object.entries(actionRequest.toolArgs ?? {}).map(([key, value]) => ({
      label: key,
      value
    }));
  }

  isSimpleActionRequestValue(value: any): boolean {
    return value === null || value === undefined || ['string', 'number', 'boolean'].includes(typeof value);
  }

  isUrlActionRequestField(field: {value: any, type?: string}): boolean {
    return field.type === 'url' && typeof field.value === 'string' && !!field.value.trim();
  }

  formatActionRequestValue(value: any): string {
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    return this.isSimpleActionRequestValue(value) ? String(value) : JSON.stringify(value, null, 2);
  }

  getMessageActionItems(message: CosoMessage): MenuItem[] {
    let actionItems = this.messageActionItems.get(message);
    if (!actionItems) {
      actionItems = [
        {
          label: 'Copy',
          icon: 'pi pi-copy',
          command: () => this.copyMessageAsFormattedText(message)
        },
        {
          label: 'Save markdown',
          icon: 'pi pi-download',
          command: () => this.saveMessageAsMarkdown(message)
        },
        {
          label: 'Add to Knowledge Base',
          icon: 'pi pi-database',
          command: () => this.openKnowledgeBaseDialogForMessage(message)
        }
      ];
      this.messageActionItems.set(message, actionItems);
    }

    return actionItems;
  }

  openKnowledgeBaseDialogForChat() {
    this.knowledgeBaseTitle = this.getFirstHumanMessage()?.content ?? '';
    this.knowledgeBaseMarkdown = this.getChatMarkdown();
    this.knowledgeBaseLanguage = this.selectedSpeechLanguage;
    this.knowledgeBaseDialogVisible = true;
    this.cdr.markForCheck();
  }

  openKnowledgeBaseDialogForMessage(message: CosoMessage) {
    this.knowledgeBaseTitle = this.getHumanMessageFor(message)?.content ?? '';
    this.knowledgeBaseMarkdown = this.getMessageMarkdown(message);
    this.knowledgeBaseLanguage = this.selectedSpeechLanguage;
    this.knowledgeBaseDialogVisible = true;
    this.cdr.markForCheck();
  }

  closeKnowledgeBaseDialog() {
    this.knowledgeBaseDialogVisible = false;
  }

  openCosoPromptDialogForMessage(message: CosoMessage) {
    const promptText = (message.content ?? '').trim();
    this.cosoPromptTitle = this.getDefaultCosoPromptTitle(promptText);
    this.cosoPromptCategory = '';
    this.cosoPromptText = promptText;
    this.cosoPromptPublic = false;
    this.cosoPromptDialogVisible = true;
    this.cdr.markForCheck();
  }

  closeCosoPromptDialog() {
    this.cosoPromptDialogVisible = false;
  }

  applyCosoPrompt(prompt: CosoPrompt) {
    if (!prompt.prompt) {
      return;
    }

    this.chatInputMessage = prompt.prompt;
    this.cosoPromptSearchQuery = '';
    this.cdr.markForCheck();
  }

  getFilteredCosoPromptOptions(): CosoPrompt[] {
    const query = this.cosoPromptSearchQuery.trim().toLowerCase();
    if (!query) {
      return this.cosoPromptOptions;
    }

    return this.cosoPromptOptions.filter(prompt => {
      return [
        prompt.title,
        prompt.category,
        prompt.prompt
      ].some(value => value?.toLowerCase().includes(query));
    });
  }

  getFilteredCosoPromptGroups(): CosoPromptGroup[] {
    const groups = new Map<string | undefined, CosoPromptGroup>();

    for (const prompt of this.getFilteredCosoPromptOptions()) {
      const group = groups.get(prompt.category);

      if (group) {
        group.prompts.push(prompt);
      } else {
        groups.set(prompt.category, {category: prompt.category, prompts: [prompt]});
      }
    }

    return Array.from(groups.values()).sort((a, b) => Number(!b.category) - Number(!a.category));;
  }

  private loadCosoPrompts() {
    const user = this.cosoService.user;
    if (!user?.user_id || this.cosoPromptsLoading) {
      return;
    }

    this.cosoPromptsLoading = true;
    this.$cosoPrompts?.unsubscribe();
    this.$cosoPrompts = this.cosoPromptsService.getChatCosoPrompts(user).subscribe({
      next: prompts => {
        this.cosoPromptOptions = prompts
          .filter(prompt => !!prompt.prompt)
          .sort((a, b) => (a.title ?? '').localeCompare(b.title ?? ''));
        this.loadedCosoPromptsForUserId = user.user_id;
        this.cosoPromptsLoading = false;
        this.cdr.markForCheck();
      },
      error: error => {
        this.cosoPromptsLoading = false;
        this.messageService.add({severity: 'error', summary: 'Error', detail: error.message, sticky: true});
        this.cdr.markForCheck();
      }
    });
  }

  private addCosoPromptOption(prompt: CosoPrompt) {
    if (!prompt.prompt) {
      return;
    }

    const index = this.cosoPromptOptions.findIndex(option => option.coso_prompts_id === prompt.coso_prompts_id);
    if (index >= 0) {
      this.cosoPromptOptions[index] = prompt;
    } else {
      this.cosoPromptOptions = [...this.cosoPromptOptions, prompt];
    }

    this.cosoPromptOptions = [...this.cosoPromptOptions]
      .sort((a, b) => (a.title ?? '').localeCompare(b.title ?? ''));
  }

  saveCosoPromptEntry() {
    const title = this.cosoPromptTitle.trim();
    const promptText = this.cosoPromptText.trim().replace(/&nbsp;|\u00A0/g, ' ');
    const userId = this.cosoService.user?.user_id;

    if (!title || !promptText || !userId || this.cosoPromptSaving) {
      return;
    }

    const prompt = new CosoPrompt();
    prompt.id_user = userId;
    prompt.title = title;
    prompt.category = this.cosoPromptCategory.trim() || undefined;
    prompt.prompt = promptText;
    prompt.flag_public = this.cosoPromptPublic;

    this.cosoPromptSaving = true;
    this.$createCosoPrompt = this.cosoPromptsService.createCosoPrompt(prompt).subscribe({
      next: async record => {
        await this.finalizeInlineImages(this.cosoPromptEditor, 'coso_prompts', record.coso_prompts_id!);
        this.$logActivity = this.activitiesService.logActivity(ActivityType.RecordCreate,
          'coso_prompts',
          record.coso_prompts_id!.toString()).subscribe({
          next: () => {
            this.addCosoPromptOption(record);
            this.cosoPromptSaving = false;
            this.cosoPromptDialogVisible = false;
            this.messageService.add({
              severity: 'success',
              summary: 'Coso prompt',
              detail: 'Prompt created.',
              sticky: false,
              life: 3000
            });
            this.cdr.markForCheck();
          }, error: error => {
            this.cosoPromptSaving = false;
            this.messageService.add({severity: 'error', summary: 'Error', detail: error.message, sticky: true});
            this.cdr.markForCheck();
          }
        })
      },
      error: error => {
        this.cosoPromptSaving = false;
        this.messageService.add({severity: 'error', summary: 'Error', detail: error.message, sticky: true});
        this.cdr.markForCheck();
      }
    });
  }

  private getDefaultCosoPromptTitle(promptText: string): string {
    const firstLine = promptText.split(/\r?\n/).find(line => line.trim())?.trim() ?? '';
    return firstLine.length > 120 ? firstLine.slice(0, 117).trimEnd() + '...' : firstLine;
  }

  saveKnowledgeBaseEntry() {
    const requestTitle = this.knowledgeBaseTitle.trim();
    const requestData = this.knowledgeBaseMarkdown.trim().replace(/&nbsp;|\u00A0/g, ' ');
    const userId = this.cosoService.user?.user_id;

    if (!requestTitle || !requestData || !userId || this.knowledgeBaseSaving) {
      return;
    }

    const request = new KBRequest();
    request.status = KBRequestStatus.Open;
    request.c_user_id = userId;
    request.request_title = requestTitle;
    request.request_data = requestData;
    request.language = this.knowledgeBaseLanguage;

    this.knowledgeBaseSaving = true;
    this.$createKnowledgeBaseRequest = this.knowledgeBaseRequestsService.createKBRequest(request).subscribe({
      next: async record => {
        await this.finalizeInlineImages(this.knowledgeBaseEditor, 'kb_requests', record.kb_requests_id!);
        this.$logActivity = this.activitiesService.logActivity(ActivityType.RecordCreate,
          'kb_requests',
          record.kb_requests_id!.toString()).subscribe({
          next: () => {
            this.knowledgeBaseSaving = false;
            this.knowledgeBaseDialogVisible = false;
            this.messageService.add({
              severity: 'success',
              summary: 'Knowledge base',
              detail: 'Request created.',
              sticky: false,
              life: 3000
            });
            this.cdr.markForCheck();
          }, error: error => {
            this.knowledgeBaseSaving = false;
            this.messageService.add({severity: 'error', summary: 'Error', detail: error.message, sticky: true});
            this.cdr.markForCheck();
          }
        })
      },
      error: error => {
        this.knowledgeBaseSaving = false;
        this.messageService.add({severity: 'error', summary: 'Error', detail: error.message, sticky: true});
        this.cdr.markForCheck();
      }
    });
  }

  private async finalizeInlineImages(editor: RichTextEditorComponent | undefined, tableName: string, fkId: string | number): Promise<void> {
    try {
      await editor?.finalizeInlineImages(tableName, fkId);
    } catch (error: any) {
      this.messageService.add({severity: 'error', summary: 'Inline image upload failed', detail: error.message, sticky: true});
    }
  }

  async copyMessageAsFormattedText(message: CosoMessage) {
    try {
      await this.copyMarkdownAsFormattedText(this.getMessageMarkdown(message));
      this.messageService.add({
        severity: 'success',
        summary: 'Message copied',
        detail: 'Message copied to the clipboard.',
        sticky: false,
        life: 3000
      });
    } catch (err) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Message failed to copy.',
        sticky: false,
        life: 5000
      });
    }
  }

  async copyChatAsFormattedText() {
    try {
      await this.copyMarkdownAsFormattedText(
        this.getChatClipboardMarkdown(),
        this.getChatPlainTextForClipboard()
      );
      this.messageService.add({
        severity: 'success',
        summary: 'Chat copied',
        detail: 'Chat copied to the clipboard.',
        sticky: false,
        life: 3000
      });
    } catch (err) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Chat failed to copy.',
        sticky: false,
        life: 5000
      });
    }
  }

  private async copyMarkdownAsFormattedText(markdownContent: string, plainText = markdownToTxt(markdownContent)) {
    if (this.canWriteFormattedClipboard()) {
      try {
        const htmlContent = await this.getClipboardHtml(markdownContent);
        await navigator.clipboard.write([
          new ClipboardItem({
            'text/html': new Blob([htmlContent], {type: 'text/html;charset=utf-8'}),
            'text/plain': new Blob([plainText], {type: 'text/plain;charset=utf-8'})
          })
        ]);
        return;
      } catch {
        // Ignore rich clipboard failures and continue to the plain-text fallback below.
      }
    }

    await navigator.clipboard.writeText(plainText);
  }

  private canWriteFormattedClipboard(): boolean {
    return typeof ClipboardItem !== 'undefined' && typeof navigator.clipboard?.write === 'function';
  }

  private async getClipboardHtml(markdownContent: string): Promise<string> {
    const renderedHtml = await this.markdownService.parse(markdownContent);
    return `<article>${renderedHtml}</article>`;
  }

  // Saves the entire chat history as a Markdown file
  saveChatAsMarkdown() {
    const markdownContent = this.getChatMarkdown();

    const blob = new Blob([markdownContent], {type: 'text/markdown;charset=utf-8'});
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `coso-chat-${new Date().toISOString().slice(0, 10)}.md`;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  private getChatMarkdown(): string {
    return this.getChatMarkdownContent();
  }

  private getChatClipboardMarkdown(): string {
    return this.getChatMarkdownContent('\n\n<br>\n\n');
  }

  private getChatPlainTextForClipboard(): string {
    return markdownToTxt(this.getChatMarkdownContent('\n\n\n'));
  }

  private getChatMarkdownContent(humanMessageSeparator = '\n\n'): string {
    let markdownContent = '';
    for (const message of this.messageHistory) {
      if (message.type === CosoMessageType.HUMAN) {
        if (markdownContent.length > 0) {
          markdownContent += humanMessageSeparator;
        }
        markdownContent += '# ' + message.content;
      } else if (message.type === CosoMessageType.AI) {
        markdownContent += '\n\n' + message.content;
      }
    }

    return markdownContent;
  }

  // Saves a single message as a Markdown file
  saveMessageAsMarkdown(message: CosoMessage) {
    const markdownContent = this.getMessageMarkdown(message);

    const blob = new Blob([markdownContent], {type: 'text/markdown;charset=utf-8'});
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `coso-message-${new Date().toISOString().slice(0, 10)}.md`;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  private getMessageMarkdown(message: CosoMessage): string {
    const humanMessage = this.getHumanMessageFor(message);

    let markdownContent = '';
    if (humanMessage) {
      markdownContent += '# ' + humanMessage.content + '\n\n';
    }

    markdownContent += message.content;
    return markdownContent;
  }

  private getFirstHumanMessage(): CosoMessage | undefined {
    return this.messageHistory.find(message => message.type === CosoMessageType.HUMAN);
  }

  private getHumanMessageFor(message: CosoMessage): CosoMessage | undefined {
    if (message.type === CosoMessageType.HUMAN) {
      return message;
    }

    const index = this.messageHistory.findIndex(m => m === message);
    if (index < 0) {
      return undefined;
    }

    for (let i = index - 1; i >= 0; i--) {
      const previousMessage = this.messageHistory[i];
      if (previousMessage.type === CosoMessageType.HUMAN) {
        return previousMessage;
      }
    }

    return undefined;
  }

  // Handles file uploads
  uploadHandler(event: FileUploadHandlerEvent) {
    if (!event.files?.length) {
      return;
    }

    for (const file of event.files) {
      if (!this.isSupportedAttachmentFile(file)) {
        this.showUnsupportedAttachmentMessage();
        continue;
      }

      if (file.size > this.MAX_ATTACHMENT_SIZE) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Attachment too large',
          detail: 'Each attachment must be 4.5 MB or smaller.',
          sticky: false,
          life: 5000
        });
        continue;
      }

      if (this.attachedFiles.length >= this.MAX_ATTACHMENTS) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Attachment limit reached',
          detail: `You can attach up to ${this.MAX_ATTACHMENTS} files.`,
          sticky: false,
          life: 3000
        });
        break;
      }
      this.addAttachedFile(file);
    }

    this.fileUploader?.clear();
    if (this.fileUploader) {
      this.fileUploader.progress = 0;
    }
    this.fileUploadPopover?.hide();
  }

  private isSupportedAttachmentFile(file: File): boolean {
    return this.isSupportedAttachmentMimeType(file.type);
  }

  private isSupportedAttachmentMimeType(mimeType: string | null | undefined): boolean {
    const normalizedMimeType = mimeType?.toLowerCase() ?? '';
    return this.getSelectedSupportedAttachmentMimeTypes().some(supportedMimeType => {
      if (supportedMimeType.endsWith('/*')) {
        return normalizedMimeType.startsWith(supportedMimeType.slice(0, -1));
      }

      return supportedMimeType === normalizedMimeType;
    });
  }

  private showUnsupportedAttachmentMessage() {
    this.messageService.add({
      severity: 'warn',
      summary: 'Unsupported file type',
      detail: `This file type is not supported. Supported file types are: ${this.getAcceptedAttachmentLabel()}.`,
      sticky: true,
      life: 5000
    });
  }

  private getSelectedCosoConfig(): CosoConfig | undefined {
    const configId = this.selectedChat?.id_coso_config ?? this.context()?.id_coso_config;
    const configs = this.allowedCosoConfigs();
    if (configId != null) {
      return configs.find(config => String(config.coso_config_id) === String(configId));
    }

    return this.cosoService.getDefaultCosoConfig();
  }

  private getAcceptedAttachmentLabel(): string {
    return this.getSelectedSupportedAttachmentMimeTypes().join(', ') || 'none configured';
  }

  getSelectedSupportedAttachmentMimeTypes(): string[] {
    const supportedMimeTypes = this.getSelectedCosoConfig()?.supported_file_mime_types;

    if (!Array.isArray(supportedMimeTypes)) {
      return [];
    }

    return Array.from(new Set(
      supportedMimeTypes
        .map(mimeType => mimeType.trim().toLowerCase())
        .filter(Boolean)
    ));
  }

  getSelectedSupportedAttachmentFileTypes(): string[] {
    const supportedMimeTypes = this.getSelectedCosoConfig()?.supported_file_mime_types;

    if (!Array.isArray(supportedMimeTypes)) {
      return [];
    }

    return Array.from(new Set(
      supportedMimeTypes
        .map(mimeType => MIME_TYPES[mimeType.trim().toLowerCase()])
    ));
  }

  // Gets the appropriate icon for a file type
  getFileIcon(file: Pick<CosoFile, 'type'>): string {
    if(file.type === CosoFileType.AUDIO) return 'pi-microphone';
    if (file.type === CosoFileType.PDF) return 'pi-file-pdf';
    if (file.type === CosoFileType.VIDEO) return 'pi-video';

    return 'pi-file';
  }

  // Removes an attached file
  removeAttachedFile(index: number) {
    const file = this.attachedFiles[index];
    if (file) {
      URL.revokeObjectURL(file.previewUrl);
    }
    this.attachedFiles = this.attachedFiles.filter((_, i) => i !== index);
    this.cdr.markForCheck();
  }

  // Determines the CosoFileType from a File object
  private getCosoFileType(file: File): CosoFileType {
    if (file.type) {
      if (file.type === "application/pdf") return CosoFileType.PDF;
      if (file.type.startsWith("image/")) return CosoFileType.IMAGE;
      if (file.type.startsWith("audio/")) return CosoFileType.AUDIO;
      if (file.type.startsWith("video/")) return CosoFileType.VIDEO;
    }

    return CosoFileType.UNSUPPORTED;
  }

  // Adds a file to the list of attachments
  private addAttachedFile(file: File) {
    this.attachedFiles.push({
      type: this.getCosoFileType(file),
      file,
      previewUrl: URL.createObjectURL(file),
      mimeType: file.type,
      fileName: file.name
    });
    this.cdr.markForCheck();
  }

  getAttachmentPreviewUrl(file: CosoHistoryFile): string | undefined {
    if ('dataUrl' in file) {
      return file.dataUrl;
    }

    const cacheKey = `${file.bucket}/${file.region}/${file.key}`;
    const cached = this.attachmentPreviewUrls.get(cacheKey);
    if (cached) {
      return cached;
    }
    if (this.loadingAttachmentPreviewKeys.has(cacheKey)) {
      return undefined;
    }

    this.loadingAttachmentPreviewKeys.add(cacheKey);
    this.attachmentPreviewSubscriptions.add(this.attachmentsService.getS3DownloadURL(
      file.bucket,
      file.key,
      file.region
    ).subscribe({
      next: url => {
        if (url) {
          this.attachmentPreviewUrls.set(cacheKey, url);
        }
        this.loadingAttachmentPreviewKeys.delete(cacheKey);
        this.cdr.markForCheck();
      },
      error: () => {
        this.loadingAttachmentPreviewKeys.delete(cacheKey);
        this.cdr.markForCheck();
      }
    }));

    return undefined;
  }

  private clearAttachedFiles() {
    for (const file of this.attachedFiles) {
      URL.revokeObjectURL(file.previewUrl);
    }
    this.attachedFiles = [];
  }

  // Starts voice recognition
  startListening() {
    console.debug('listening...');
    this.voiceService.startListening();
    this.listening = true;
    this.speechEnabled = true;
  }

  // Stops voice recognition
  stopListening(discardPendingResults: boolean = false) {
    console.debug('Done listening');
    this.listening = false;
    if(discardPendingResults) {
      this.voiceService.abortListening();
      return;
    }
    this.voiceService.stopListening();
  }

  toggleCosoLive() {
    if (this.liveActive) {
      this.stopCosoLive();
      return;
    }

    this.startCosoLive();
  }

  private startCosoLive() {
    if (this.voiceControlMode === 'dictate' || !this.isVoiceControlModeAvailable(this.voiceControlMode)) {
      this.updateVoiceControlModes();
      return;
    }

    const currentContext = this.context();
    const userId = this.cosoService.user?.user_id;
    if (this.streamingResponse || !currentContext || !userId) {
      return;
    }
    const context: CosoContext = {
      ...currentContext,
      language: this.selectedSpeechLanguage
    };

    if (this.listening) {
      this.stopListening(true);
    }
    if (this.speaking) {
      this.stopSpeech();
    }

    this.liveActive = true;
    if (this.voiceControlMode === 'video') {
      this.bottomSpacerHeight = 0;
    }
    void this.requestScreenWakeLock();
    this.$liveSession?.unsubscribe();
    this.$liveSession = this.cosoLiveService.start(context, userId, {
      video: this.voiceControlMode === 'video'
    }).subscribe({
      next: cosoStream => this.applyCosoLiveStream(cosoStream),
      error: error => {
        this.liveActive = false;
        this.releaseScreenWakeLock();
        this.messageService.add({
          severity: 'error',
          summary: 'Live error',
          detail: error.message,
          sticky: false,
          life: 5000
        });
        this.cdr.markForCheck();
      },
      complete: () => {
        this.liveActive = false;
        this.releaseScreenWakeLock();
        this.cdr.markForCheck();
      }
    });
    this.cdr.markForCheck();
  }

  private isLiveVideoVisible(): boolean {
    return this.voiceControlMode === 'video' && this.liveActive && !!this.liveMediaStream();
  }

  // With the live video on, the chat is a short strip: keep it pinned to the bottom
  // on every DOM change (streamed text, async markdown render, layout switch)
  private observeChatForLiveVideo() {
    const chatContainer = this.chat?.nativeElement;
    if (!chatContainer) {
      return;
    }
    this.chatMutationObserver = new MutationObserver(() => {
      if (this.isLiveVideoVisible()) {
        chatContainer.scrollTop = chatContainer.scrollHeight;
      }
    });
    this.chatMutationObserver.observe(chatContainer, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['class']
    });
  }

  async switchCamera() {
    try {
      const cameras = await this.cosoLiveService.getVideoInputs();
      if (cameras.length < 2) {
        return;
      }
      const currentId = this.liveMediaStream()?.getVideoTracks()[0]?.getSettings().deviceId;
      const index = cameras.findIndex(camera => camera.deviceId === currentId);
      await this.cosoLiveService.switchCamera(cameras[(index + 1) % cameras.length].deviceId);
    } catch (error) {
      console.error('[CosoLive] Camera switch failed', error);
    }
  }

  stopCosoLive() {
    this.$liveSession?.unsubscribe();
    this.cosoLiveService.stop();
    this.liveActive = false;
    this.releaseScreenWakeLock();
    this.cdr.markForCheck();
  }

  private async requestScreenWakeLock() {
    const wakeLock = 'wakeLock' in navigator ? navigator.wakeLock : undefined;
    if (!wakeLock || this.screenWakeLock || document.visibilityState !== 'visible') {
      return;
    }

    try {
      const screenWakeLock = await wakeLock.request('screen');
      if (!this.liveActive) {
        await screenWakeLock.release();
        return;
      }

      this.screenWakeLock = screenWakeLock;
      screenWakeLock.addEventListener('release', () => {
        if (this.screenWakeLock === screenWakeLock) {
          this.screenWakeLock = undefined;
        }
      });
    } catch (error) {
      console.warn('[CosoLive] Screen wake lock unavailable:', error);
    }
  }

  private releaseScreenWakeLock() {
    const screenWakeLock = this.screenWakeLock;
    this.screenWakeLock = undefined;
    screenWakeLock?.release().catch(() => {});
  }

  private applyCosoLiveStream(cosoStream: CosoStream) {
    if (cosoStream.type === CosoStreamType.HUMAN_MESSAGE) {
      const lastMessage = this.messageHistory[this.messageHistory.length - 1];
      if (lastMessage?.type === CosoMessageType.HUMAN) {
        lastMessage.content = cosoStream.content || lastMessage.content || '';
      } else {
        this.messageHistory.push({
          type: CosoMessageType.HUMAN,
          date: new Date(cosoStream.timestamp),
          content: cosoStream.content || '',
          files: cosoStream.files || [],
          author: 'you'
        });
        this.pendingScrollToLastHumanMessage = !this.isLiveVideoVisible();
      }
    } else if (
      cosoStream.type === CosoStreamType.AI_PARTIAL_MESSAGE ||
      cosoStream.type === CosoStreamType.MODEL_ERROR ||
      cosoStream.type === CosoStreamType.TOOL_REQUEST ||
      cosoStream.type === CosoStreamType.TOOL_RESPONSE ||
      cosoStream.type === CosoStreamType.INTERRUPT ||
      cosoStream.type === CosoStreamType.REASONING
    ) {
      let aiMessage = this.messageHistory[this.messageHistory.length - 1];
      if (aiMessage?.type !== CosoMessageType.AI) {
        if (aiMessage?.type === CosoMessageType.HUMAN) {
          this.renameDefaultChatFromFirstMessage(aiMessage.content);
        }
        aiMessage = {
          type: CosoMessageType.AI,
          date: new Date(),
          author: 'Coso',
          content: ''
        };
        this.messageHistory.push(aiMessage);
      }
      this.applyCosoStreamToAiMessage(cosoStream, aiMessage);
    }

    this.cdr.markForCheck();
  }

  // Stops the text to speech
  stopSpeech() {
    this.speechPlaybackToken++;
    this.speechStoppedForCurrentResponse = true;
    this.speaking = false;
    this.speechService.stop();
    this.cdr.markForCheck();
  }

  playSpeech(message: CosoMessage) {
    const content = message.content?.trim();
    if (!content || this.speaking) {
      return;
    }

    if (this.listening) {
      this.stopListening(true);
    }

    this.speechStoppedForCurrentResponse = true;
    this.speechPlaybackToken++;
    const playbackToken = this.speechPlaybackToken;

    this.speechService.stop();
    this.speechService.unlockAudio();
    this.speaking = true;
    this.speechService.processChunk(content);
    this.speechService.flush().then(() => {
      if (playbackToken !== this.speechPlaybackToken) {
        return;
      }
      this.speaking = false;
      this.cdr.markForCheck();
    });
    this.cdr.markForCheck();
  }

  shouldShowSpeechControl(message: CosoMessage, isLastMessage: boolean, isStreamingMessage: boolean): boolean {
    return this.speechEnabled
      && isLastMessage
      && !!message.content?.trim()
      && (this.speaking || !isStreamingMessage || this.speechStoppedForCurrentResponse);
  }

  // Updates the bottom spacer height for the last human message
  private updateBottomSpacerForLastHumanMessage(): void {
    setTimeout(() => {
      const lastHumanItem = this.humanMessageItems.last;
      const chatContainer = this.chat?.nativeElement;
      if (!lastHumanItem || !chatContainer) {
        return;
      }

      const requiredHeight = this.getRequiredBottomSpacerHeight(chatContainer, lastHumanItem.nativeElement);
      if (requiredHeight !== this.bottomSpacerHeight) {
        this.bottomSpacerHeight = requiredHeight;
        this.cdr.markForCheck();
      }
    });
  }

  // Calculates the required height for the bottom spacer
  private getRequiredBottomSpacerHeight(container: HTMLElement, target: HTMLElement): number {
    if (this.isLiveVideoVisible()) {
      return 0;
    }
    const targetTop = this.getElementTopInContainer(container, target);
    const baseScrollHeight = container.scrollHeight - this.bottomSpacerHeight;
    const maxScrollTop = Math.max(0, baseScrollHeight - container.clientHeight);
    const required = targetTop - maxScrollTop;
    return required > 0 ? Math.ceil(required) : 0;
  }

  // Calculates an element top offset relative to the chat scroll container.
  private getElementTopInContainer(container: HTMLElement, target: HTMLElement): number {
    const containerRect = container.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    return targetRect.top - containerRect.top + container.scrollTop;
  }

  // Expose CosoFileType to the template
  protected readonly CosoFileType = CosoFileType;

}
